import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import client from '../api/client';
import { Heart, Calendar, MapPin, ExternalLink, Mail, Loader2, ShieldCheck, Link2, Check, Play, Pause, Music2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Uploaded files come back from the API as relative paths ("/api/files/…").
// On our deployed custom domain the frontend and backend can be on
// different origins, so we always resolve upload paths against the backend
// URL. External URLs (Unsplash, Pexels, etc.) are returned as-is.
const resolveMedia = (u) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
};

const FONT_BY_TEMPLATE = {
  minimal: { font: "'Inter', sans-serif",            serif: "'Cormorant Garamond', serif" },
  rustic:  { font: "'Inter', sans-serif",            serif: "'Cormorant Garamond', serif" },
  classic: { font: "'Inter', sans-serif",            serif: "'Cormorant Garamond', serif" },
  boho:    { font: "'Inter', sans-serif",            serif: "'Cormorant Garamond', serif" },
  garden:  { font: "'Inter', sans-serif",            serif: "'Cormorant Garamond', serif" },
  coastal: { font: "'Inter', sans-serif",            serif: "'Cormorant Garamond', serif" },
};

// Instagram-style caption overlay. Rendered absolutely inside a
// `position:relative` photo wrapper that has `container-type: inline-size`
// set (so `cqw` units work). Position is stored as x/y percentages and
// font size is stored in "px @ 640px reference width" so it scales
// proportionally on any photo container size — otherwise a 36px caption
// stored in the editor's wide preview would wrap and clip on a 350px
// mobile photo card.
function PhotoCaptionOverlay({ caption, testId }) {
  if (!caption || !caption.enabled || !caption.text) return null;
  const align = caption.align || 'center';
  const translate = align === 'left'
    ? 'translate(0, -50%)'
    : align === 'right'
      ? 'translate(-100%, -50%)'
      : 'translate(-50%, -50%)';
  // Convert stored px-@-640 → percent of container inline size. Using cqw
  // keeps the caption visually identical between the editor preview and
  // the public site regardless of the photo card's actual width.
  const cqw = ((caption.fontSize || 28) / 640) * 100;
  const style = {
    position: 'absolute',
    left: `${caption.x ?? 50}%`,
    top: `${caption.y ?? 85}%`,
    transform: translate,
    color: caption.color || '#FFFFFF',
    fontSize: `${cqw}cqw`,
    fontWeight: caption.weight === 'normal' ? 400 : 700,
    fontStyle: caption.italic ? 'italic' : 'normal',
    textAlign: align,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.15,
    maxWidth: '90%',
    textShadow: caption.shadow === false ? 'none' : '0 2px 12px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.45)',
    pointerEvents: 'none',
    userSelect: 'none',
  };
  return (
    <div data-testid={testId} style={style}>{caption.text}</div>
  );
}

export default function WeddingSite() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const [site, setSite] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        if (isPreview) {
          // Owner-only preview — works even when the site is unpublished.
          const { data } = await client.get('/website/preview');
          setSite(data);
        } else {
          const { data } = await axios.get(`${API}/website/public/${slug}`);
          setSite(data);
          // Fire-and-forget aggregate visit (no PII, just a day counter)
          axios.post(`${API}/website/track-visit/${slug}`).catch(() => { /* ignore */ });
        }
      } catch (e) {
        setError(e?.response?.data?.detail || 'Site not found');
      }
    })();
  }, [slug, isPreview]);

  if (error) {
    const isPrivate = /private/i.test(String(error));
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <Heart className="w-8 h-8 text-[#7B2C3B] mx-auto" />
          <h1 className="font-serif text-3xl mt-3 text-[#2A2A2A]">{error}</h1>
          {isPrivate ? (
            <>
              <p className="text-sm text-[#6A6A6A] mt-2">The couple hasn&apos;t published the site yet. If this is your site, head to <span className="text-[#7B2C3B] font-medium">Dashboard → Website</span> and hit the <span className="text-[#7B2C3B] font-medium">Private</span> toggle to publish, or open <Link to={`/wedding/${slug}?preview=1`} className="text-[#7B2C3B] underline">Preview mode</Link>.</p>
              <Link to="/dashboard" className="mt-5 inline-flex items-center gap-2 text-sm text-[#7B2C3B] hover:underline">Go to your dashboard</Link>
            </>
          ) : (
            <>
              <p className="text-sm text-[#6A6A6A] mt-2">The link may be incorrect.</p>
              <Link to="/" className="mt-5 inline-flex items-center gap-2 text-sm text-[#7B2C3B] hover:underline">Back to Yourweddingfriends</Link>
            </>
          )}
        </div>
      </div>
    );
  }
  if (!site) {
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#7B2C3B]" />
      </div>
    );
  }

  const accent = site.accent || '#7B2C3B';
  const wash = site.wash || '#F4E4DA';
  const fonts = FONT_BY_TEMPLATE[site.template] || FONT_BY_TEMPLATE.classic;
  const dateLabel = site.weddingDate ? new Date(site.weddingDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div
      data-testid="public-wedding-site"
      style={{ fontFamily: fonts.font, backgroundColor: '#FBF7F2', color: '#2A2A2A' }}
    >
      {isPreview && (
        <div data-testid="preview-mode-banner" className="sticky top-0 z-40 bg-[#2A2A2A] text-[#FBF7F2] text-xs py-2 px-4 text-center">
          <span className="opacity-80">Preview mode</span> — only you can see this. Head back to <Link to="/dashboard" className="underline">your dashboard</Link> and hit <em>Publish</em> to share it with guests.
        </div>
      )}
      {/* Background music player — only mounts when the couple has set a
          song. Muted-autoplay ensures the site loads without a browser
          block; the floating pill lets guests turn sound on with one tap. */}
      {site.musicUrl && (
        <BackgroundMusicPlayer
          src={site.musicUrl}
          title={site.musicTitle}
          artist={site.musicArtist}
          source={site.musicSource}
          accent={accent}
        />
      )}
      {/* Hero — content anchored ~75% down the frame so the couple's faces
          have room to breathe above the type. When the hero photo URL fails
          to load (broken /api/files/ path on a cross-origin deploy) we fall
          back to a colored wash that still looks intentional. */}
      <HeroSection
        photoUrl={site.heroPhoto ? resolveMedia(site.heroPhoto) : ''}
        wash={wash}
        accent={accent}
        caption={site.heroCaption}
      >
        {({ showPhoto }) => (
          <div className="relative z-10 max-w-3xl">
            <HeroHeading site={site} accent={accent} fonts={fonts} dateLabel={dateLabel} hasPhotoBg={showPhoto} />
          </div>
        )}
      </HeroSection>

      {/* Sticky RSVP banner — always available, no matter which page a guest lands on */}
      <div data-testid="site-rsvp-cta" className="sticky top-0 z-30 w-full" style={{ background: accent, color: '#FBF7F2' }}>
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
          <p className="text-xs sm:text-sm inline-flex items-center gap-2 min-w-0 truncate">
            <Heart className="w-3.5 h-3.5 fill-current flex-shrink-0" />
            <span className="truncate">Haven&apos;t replied yet?</span>
          </p>
          <a
            data-testid="site-rsvp-link"
            href="#rsvp"
            className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full bg-white flex-shrink-0"
            style={{ color: accent }}
          >
            RSVP <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Countdown strip — hides after the wedding day */}
      <GuestCountdown weddingDate={site.weddingDate} tagline={site.countdownTagline} accent={accent} wash={wash} fonts={fonts} />

      {/* Story */}
      {site.sections?.story && (
        <Section title="Our story" accent={accent} fonts={fonts}>
          {(site.storyPhoto || site.couplePhoto) && (
            <div className="max-w-sm mx-auto mb-8">
              <div className="relative w-full aspect-[3/4] rounded-3xl overflow-hidden shadow-md" style={{ containerType: 'inline-size' }}>
                <img
                  data-testid="site-story-photo"
                  src={resolveMedia(site.storyPhoto || site.couplePhoto)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <PhotoCaptionOverlay
                  caption={site.storyPhoto ? site.storyCaption : (site.storyCaption || site.coupleCaption)}
                  testId="site-story-caption"
                />
              </div>
            </div>
          )}
          <p className="text-base sm:text-lg leading-relaxed text-[#3A3A3A] whitespace-pre-wrap max-w-2xl mx-auto text-center">
            {site.storyText || ''}
          </p>
        </Section>
      )}

      {/* Schedule */}
      {site.sections?.schedule && (site.schedule || []).length > 0 && (
        <Section title="The day" accent={accent} fonts={fonts} bg={wash}>
          <div className="max-w-xl mx-auto space-y-4">
            {site.schedule.map((row, i) => (
              <div key={i} className="grid grid-cols-12 items-baseline gap-4 py-2 border-b border-[#EFE3D8]/60 last:border-0">
                <div className="col-span-3 text-sm font-medium" style={{ color: accent }}>{row.time}</div>
                <div className="col-span-9">
                  <p className="text-lg" style={{ fontFamily: fonts.serif }}>{row.title}</p>
                  {row.where && <p className="text-xs text-[#6A6A6A]">{row.where}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Travel */}
      {site.sections?.travel && (site.travel || (site.hotels && site.hotels.length > 0) || (site.rideshare && ((site.rideshare.providers || []).some(p => p.name) || site.rideshare.shuttleNote))) && (
        <Section title="Travel" accent={accent} fonts={fonts} bg="white">
          {site.travel && (
            <p className="text-center max-w-2xl mx-auto whitespace-pre-wrap leading-relaxed text-[#2A2A2A]/85">
              {site.travel}
            </p>
          )}
          {site.hotels && site.hotels.length > 0 && (
            <>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
                {site.hotels.map((h, i) => (
                  <a
                    key={i}
                    href={h.url || '#'}
                    target={h.url ? '_blank' : undefined}
                    rel={h.url ? 'noreferrer' : undefined}
                    className="text-left p-4 rounded-2xl bg-white transition-transform hover:-translate-y-0.5"
                    style={{ border: `1px solid ${accent}33` }}
                  >
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: accent }}>Hotel</p>
                    <p className="text-lg mt-1" style={{ fontFamily: fonts.serif }}>{h.name}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-[#6A6A6A]">
                      {h.distance && <span>{h.distance}</span>}
                      {h.priceRange && <span>· {h.priceRange}</span>}
                    </div>
                    {h.notes && <p className="text-xs mt-2 text-[#6A6A6A]">{h.notes}</p>}
                    {h.url && (
                      <span className="mt-2 inline-flex items-center gap-1 text-xs" style={{ color: accent }}>
                        Book <ExternalLink className="w-3 h-3" />
                      </span>
                    )}
                  </a>
                ))}
              </div>
              {/* Shareable page link so any guest (or their travel buddy) can
                  copy the full URL of this wedding site with one click. */}
              <ShareThisPageStrip accent={accent} label="Share this hotel list with a travel buddy" />
            </>
          )}
          {site.rideshare && ((site.rideshare.providers && site.rideshare.providers.length > 0) || site.rideshare.shuttleNote) && (
            <div className="mt-10 max-w-3xl mx-auto">
              <p className="text-xs uppercase tracking-wider text-center mb-4" style={{ color: accent }}>Getting around</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(site.rideshare.providers || []).filter(p => p.name).map((p, i) => (
                  <a
                    key={i}
                    href={p.url || '#'}
                    target={p.url ? '_blank' : undefined}
                    rel={p.url ? 'noreferrer' : undefined}
                    className="text-left p-4 rounded-2xl bg-white transition-transform hover:-translate-y-0.5"
                    style={{ border: `1px solid ${accent}33` }}
                  >
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: accent }}>Ride share</p>
                    <p className="text-lg mt-1" style={{ fontFamily: fonts.serif }}>{p.name}</p>
                    {p.code && (
                      <div className="mt-2 text-xs text-[#6A6A6A]">
                        Promo code: <span className="font-mono px-1.5 py-0.5 rounded bg-[#FBF7F2]">{p.code}</span>
                      </div>
                    )}
                    {p.note && <p className="text-xs mt-2 text-[#6A6A6A]">{p.note}</p>}
                  </a>
                ))}
              </div>
              {site.rideshare.shuttleNote && (
                <div className="mt-4 p-4 rounded-2xl bg-white text-center" style={{ border: `1px solid ${accent}33` }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: accent }}>Group shuttle</p>
                  <p className="text-sm mt-2 text-[#3A3A3A]">{site.rideshare.shuttleNote}</p>
                  {site.rideshare.shuttleUrl && (
                    <a href={site.rideshare.shuttleUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs" style={{ color: accent }}>
                      Details <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Registry */}
      {site.sections?.registry && (site.registryLinks || []).length > 0 && (
        <Section title="Registry" accent={accent} fonts={fonts} bg={wash}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {site.registryLinks.map((r, i) => {
              const isCash = r.type === 'cash';
              return (
                <a
                  key={i}
                  href={r.url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="p-4 rounded-2xl flex flex-col items-start gap-2 text-left transition-transform hover:-translate-y-0.5"
                  style={{ background: 'white', border: `1px solid ${accent}33` }}
                >
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: accent }}>
                    {isCash ? 'Cash fund' : 'Registry'}
                  </span>
                  <span className="text-lg" style={{ fontFamily: fonts.serif }}>{r.label || (isCash ? 'Honeymoon Fund' : 'Registry')}</span>
                  {isCash && r.goal && (
                    <span className="text-xs text-[#6A6A6A]">Goal: ${Number(r.goal).toLocaleString()}</span>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs" style={{ color: accent }}>
                    {isCash ? 'Contribute' : 'Open'} <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </a>
              );
            })}
          </div>
          <div className="text-center mt-6"><Link data-testid="wedding-site-gift-page-link" to={`/gifts/${slug}`} className="inline-block px-6 py-3 rounded-full text-sm text-white" style={{ background: accent }}>Give to our funds by card</Link></div>
          <p className="text-center mt-6 text-xs text-[#9A8A7E]">No referral fees on any link — we never sell your guests&apos; data.</p>
        </Section>
      )}

      {/* FAQ */}
      {site.sections?.faq && (site.faq || []).length > 0 && (
        <Section title="Good to know" accent={accent} fonts={fonts}>
          <div className="max-w-2xl mx-auto space-y-4">
            {site.faq.map((row, i) => (
              <div key={i} className="text-left">
                <p className="text-lg" style={{ fontFamily: fonts.serif, color: accent }}>{row.q}</p>
                <p className="text-sm text-[#3A3A3A] mt-1 leading-relaxed">{row.a}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* RSVP CTA — always shown (guests need a way to reply from any surface) */}
      <Section title="" accent={accent} fonts={fonts} bg={wash}>
        <div id="rsvp" className="text-center max-w-md mx-auto scroll-mt-24">
          <p style={{ fontFamily: fonts.serif }} className="text-3xl mb-2">Will you join us?</p>
          <p className="text-sm text-[#4A4A4A] mb-5">Tap the button in the email or text we sent you to RSVP in one tap. Lost it? Drop us a line and we&apos;ll re-send.</p>
          <a data-testid="site-rsvp-email" href="mailto:" onClick={(e) => e.preventDefault()} className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium" style={{ background: accent, color: '#FBF7F2' }}>
            <Mail className="w-4 h-4" /> Reach out
          </a>
        </div>
      </Section>

      {/* Footer */}
      <footer className="px-6 py-10 text-center text-xs" style={{ color: '#6A6A6A' }}>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#EFE3D8] bg-white">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: accent }} />
          We never share your details — privacy first.
        </div>
        <p className="mt-4 inline-flex items-center gap-1.5">
          Made with <Heart className="w-3 h-3" style={{ fill: accent, color: accent }} /> on
          <Link to="/" className="hover:underline" style={{ color: accent }}>Yourweddingfriends</Link>
        </p>
      </footer>

    </div>
  );
}

// -----------------------------------------------------------------------------
// HeroSection — CSS background-images fail SILENTLY when the URL 404s (no
// error event, no onError hook). So we preload the image via a hidden <img>
// element which DOES fire onError, then conditionally use it as the background.
// If it fails, we fall back to a colored wash + soft radial accent so the
// site still looks intentional (never gray-with-white-text).
// -----------------------------------------------------------------------------
function HeroSection({ photoUrl, wash, accent, caption, children }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    if (!photoUrl) { setLoaded(false); setErrored(false); return; }
    setLoaded(false);
    setErrored(false);
    const img = new Image();
    img.onload = () => setLoaded(true);
    img.onerror = () => setErrored(true);
    img.src = photoUrl;
    return () => { img.onload = null; img.onerror = null; };
  }, [photoUrl]);

  const showPhoto = photoUrl && loaded && !errored;
  const style = showPhoto
    ? { background: `linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.55) 65%), url(${photoUrl}) center/cover` }
    : { background: wash };

  return (
    <section
      data-testid="site-hero"
      data-hero-state={showPhoto ? 'photo' : (errored ? 'photo-error' : 'no-photo')}
      className="relative min-h-[80vh] flex items-end justify-center text-center px-6 pb-[8vh] sm:pb-[10vh] overflow-hidden"
      style={{ ...style, containerType: 'inline-size' }}
    >
      {!showPhoto && (
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(circle at 30% 20%, ${accent}22, transparent 60%), radial-gradient(circle at 80% 80%, ${accent}33, transparent 60%)` }}
        />
      )}
      {showPhoto && <PhotoCaptionOverlay caption={caption} testId="site-hero-caption" />}
      {typeof children === 'function' ? children({ showPhoto }) : children}
    </section>
  );
}

function HeroHeading({ site, accent, fonts, dateLabel, hasPhotoBg }) {
  return (
    <>
      <h1 className="text-5xl sm:text-7xl leading-tight" style={{ fontFamily: fonts.serif, color: hasPhotoBg ? 'white' : '#2A2A2A' }}>
        {site.coverHeadline || 'You & me'}
      </h1>
      {dateLabel && (
        <p className="mt-4 text-base sm:text-lg inline-flex items-center gap-2" style={{ color: hasPhotoBg ? 'white' : '#4A4A4A' }}>
          <Calendar className="w-4 h-4" /> {dateLabel}
        </p>
      )}
      {(site.venue || site.location) && (
        <p className="mt-1 text-sm sm:text-base inline-flex items-center gap-2" style={{ color: hasPhotoBg ? 'white' : '#6A6A6A' }}>
          <MapPin className="w-4 h-4" /> {[site.venue, site.location].filter(Boolean).join(' · ')}
        </p>
      )}
      {site.venueAddress && (
        <p className="mt-2 text-xs sm:text-sm" style={{ color: hasPhotoBg ? 'white' : '#6A6A6A' }}>
          <a
            data-testid="site-venue-address"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(site.venueAddress)}`}
            target="_blank"
            rel="noreferrer"
            className="underline decoration-dotted underline-offset-4 hover:opacity-90 inline-flex items-center gap-1"
            style={{ color: hasPhotoBg ? 'white' : accent }}
          >
            {site.venueAddress} <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      )}
      <div className="mt-5 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs" style={{ background: hasPhotoBg ? 'rgba(255,255,255,0.92)' : 'white', color: accent }}>
        <Heart className="w-3.5 h-3.5" style={{ fill: accent, color: accent }} /> Together forever
      </div>
    </>
  );
}


// Little copy-URL strip we can drop into any section of the public wedding
// site so guests can forward the page to a travel buddy in one click.
function ShareThisPageStrip({ accent, label }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      ta.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div data-testid="share-this-page" className="mt-8 max-w-3xl mx-auto">
      <div className="rounded-2xl bg-white p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3" style={{ border: `1px solid ${accent}33` }}>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: accent }}>
            <Link2 className="inline w-3 h-3 mr-1 -mt-0.5" /> {label || 'Share this page'}
          </p>
          <p data-testid="share-this-page-url" className="text-xs sm:text-sm text-[#3A3A3A] font-mono truncate">{url}</p>
        </div>
        <button
          data-testid="share-this-page-btn"
          onClick={copy}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition-all"
          style={{ background: copied ? '#4A7A4A' : accent, color: 'white' }}
        >
          {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Link2 className="w-3.5 h-3.5" /> Copy link</>}
        </button>
      </div>
    </div>
  );
}


// Floating background-music player for the public wedding site.
// Two modes:
//   • MP3 upload → HTML5 <audio> autoplay MUTED + toggle pill (guest tap unmutes)
//   • Streaming link (Spotify / YouTube / etc.) → embedded provider iframe
//     tucked into a collapsible mini-player pill (Spotify/YouTube honour
//     their own autoplay rules; a play-toggle mounts/unmounts the iframe).
function BackgroundMusicPlayer({ src, title, artist, source, accent }) {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  const [audioError, setAudioError] = React.useState(false);
  // For streaming: whether the mini-player is expanded (mounts the iframe).
  const [expanded, setExpanded] = React.useState(false);

  const isUpload = !source || source === 'upload';

  const audioSrc = isUpload
    ? (src?.startsWith('http') ? src : `${process.env.REACT_APP_BACKEND_URL || ''}${src || ''}`)
    : '';

  // Convert a public streaming URL → embeddable iframe URL.
  const embedUrl = React.useMemo(() => {
    if (isUpload || !src) return '';
    try {
      const u = new URL(src);
      const host = u.hostname.toLowerCase();
      // Spotify: /track/ID, /album/ID, /playlist/ID → embed prefix /embed/
      if (host.endsWith('spotify.com')) {
        return `https://open.spotify.com/embed${u.pathname}${u.search}`;
      }
      // YouTube: youtu.be/ID → youtube.com/embed/ID
      if (host === 'youtu.be') {
        const id = u.pathname.replace(/^\//, '').split('/')[0];
        return `https://www.youtube.com/embed/${id}?autoplay=1&loop=1&playlist=${id}`;
      }
      if (host.endsWith('youtube.com')) {
        // /watch?v=ID  → /embed/ID
        const id = u.searchParams.get('v');
        const list = u.searchParams.get('list');
        if (list) {
          return `https://www.youtube.com/embed/videoseries?list=${list}&autoplay=1&loop=1`;
        }
        if (id) {
          return `https://www.youtube.com/embed/${id}?autoplay=1&loop=1&playlist=${id}`;
        }
        return src;
      }
      // Apple Music: /album/name/id → /embed/album/name/id
      if (host.endsWith('apple.com')) {
        if (u.pathname.startsWith('/embed/')) return src;
        return `https://embed.music.apple.com${u.pathname}${u.search}`;
      }
      // SoundCloud: use their oEmbed player
      if (host.endsWith('soundcloud.com')) {
        return `https://w.soundcloud.com/player/?url=${encodeURIComponent(src)}&auto_play=true&color=%237B2C3B`;
      }
      // Pandora doesn't allow embedding — we render an "Open on Pandora" pill instead
      return src;
    } catch {
      return src;
    }
  }, [src, isUpload]);

  const canEmbed = !isUpload && source && source !== 'pandora' && source !== 'other';
  const providerName = ({ spotify: 'Spotify', youtube: 'YouTube', apple: 'Apple Music', pandora: 'Pandora', soundcloud: 'SoundCloud' })[source] || 'Music';

  // MP3 autoplay-muted on mount. Modern browsers block audible autoplay
  // for first-time visitors — so we boot silently, then unmute on the
  // guest's very first interaction (any click, tap, scroll, keypress).
  // This makes it feel automatic: the moment they glance around the page,
  // music fades in.
  React.useEffect(() => {
    if (!isUpload) return;
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.55;
    a.muted = true;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));

    let armed = true;
    const unmuteOnFirstGesture = () => {
      if (!armed) return;
      armed = false;
      const el = audioRef.current;
      if (!el) return;
      el.muted = false;
      setMuted(false);
      el.play().then(() => setPlaying(true)).catch(() => { /* user still needs to tap the pill */ });
    };
    const opts = { once: true, passive: true, capture: true };
    window.addEventListener('pointerdown', unmuteOnFirstGesture, opts);
    window.addEventListener('touchstart', unmuteOnFirstGesture, opts);
    window.addEventListener('keydown', unmuteOnFirstGesture, opts);
    window.addEventListener('scroll', unmuteOnFirstGesture, opts);
    return () => {
      armed = false;
      window.removeEventListener('pointerdown', unmuteOnFirstGesture, true);
      window.removeEventListener('touchstart', unmuteOnFirstGesture, true);
      window.removeEventListener('keydown', unmuteOnFirstGesture, true);
      window.removeEventListener('scroll', unmuteOnFirstGesture, true);
    };
  }, [audioSrc, isUpload]);

  const toggleAudio = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.muted = false;
      setMuted(false);
      try {
        await a.play();
        setPlaying(true);
      } catch { /* user-gesture required */ }
    } else if (muted) {
      a.muted = false;
      setMuted(false);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const label = title
    ? (artist ? `${title} — ${artist}` : title)
    : (isUpload ? 'Play music' : `Play on ${providerName}`);

  // ---------- Streaming (embed) branch ----------
  if (!isUpload) {
    // Pandora / unknown → fallback to a link-out pill (they block iframes).
    if (!canEmbed) {
      return (
        <a
          data-testid="site-music-openlink"
          href={src}
          target="_blank"
          rel="noreferrer"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg backdrop-blur-md transition-all hover:scale-105"
          style={{ background: 'rgba(255,255,255,0.92)', color: accent, border: `1px solid ${accent}55` }}
        >
          <Music2 className="w-4 h-4" />
          <span className="text-xs font-medium max-w-[220px] truncate">{label}</span>
        </a>
      );
    }
    return (
      <div className="fixed bottom-5 right-5 z-40" data-testid="site-music-embed">
        {expanded && (
          <div className="mb-2 rounded-2xl overflow-hidden shadow-xl border" style={{ borderColor: `${accent}55`, background: 'white', width: source === 'youtube' ? 360 : 320 }}>
            <iframe
              data-testid="site-music-iframe"
              title="Wedding background music"
              src={embedUrl}
              width="100%"
              height={source === 'spotify' ? 152 : source === 'youtube' ? 202 : 175}
              frameBorder="0"
              allow="autoplay; encrypted-media; clipboard-write; fullscreen; picture-in-picture"
              loading="lazy"
              style={{ display: 'block', border: 0 }}
            />
          </div>
        )}
        <button
          type="button"
          data-testid="site-music-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Hide music player' : 'Show music player'}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg backdrop-blur-md transition-all hover:scale-105"
          style={{ background: expanded ? accent : 'rgba(255,255,255,0.92)', color: expanded ? 'white' : accent, border: `1px solid ${accent}55` }}
        >
          {expanded ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          <span className="text-xs font-medium max-w-[220px] truncate">
            {expanded ? (title ? label : `${providerName}: now playing`) : label}
          </span>
          {expanded && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
          )}
        </button>
      </div>
    );
  }

  // ---------- MP3 upload branch ----------
  const isOn = playing && !muted;
  if (audioError) return null;
  return (
    <>
      <audio
        ref={audioRef}
        src={audioSrc}
        loop
        preload="auto"
        muted={muted}
        onError={() => setAudioError(true)}
        data-testid="site-bg-audio"
      />
      <button
        type="button"
        data-testid="site-music-toggle"
        onClick={toggleAudio}
        aria-label={isOn ? 'Pause music' : 'Play music'}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg backdrop-blur-md transition-all hover:scale-105"
        style={{ background: isOn ? accent : 'rgba(255,255,255,0.92)', color: isOn ? 'white' : accent, border: `1px solid ${accent}55` }}
      >
        {isOn ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        <span className="text-xs font-medium max-w-[220px] truncate">
          {isOn ? label : 'Play music'}
        </span>
        {isOn && <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-white" /></span>}
      </button>
    </>
  );
}


function Section({ title, accent, fonts, bg, children }) {
  return (
    <section className="px-6 py-16 sm:py-20" style={{ background: bg || 'transparent' }}>
      {title && (
        <div className="text-center mb-10">
          <p className="uppercase tracking-[0.3em] text-[10px]" style={{ color: accent }}>{title}</p>
          <div className="mt-2 w-10 h-px mx-auto" style={{ background: accent }} />
        </div>
      )}
      {children}
    </section>
  );
}

function GuestCountdown({ weddingDate, tagline, accent, wash, fonts }) {
  if (!weddingDate) return null;
  const d = new Date(weddingDate + 'T00:00:00');
  if (isNaN(d)) return null;
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.ceil((d - now) / msPerDay);
  if (days < 0) return null; // hide after the wedding
  let big, small;
  if (days === 0) { big = "It's today"; small = "See you at the ceremony."; }
  else if (days === 1) { big = 'Tomorrow'; small = 'One more sleep.'; }
  else if (days <= 7) { big = `${days} days to go`; small = 'The countdown is officially on.'; }
  else if (days <= 30) { big = `${days} days to go`; small = 'Just around the corner.'; }
  else if (days <= 180) { const m = Math.round(days / 30); big = `${days} days to go`; small = `About ${m} month${m === 1 ? '' : 's'} away.`; }
  else { const m = Math.round(days / 30); big = `${days} days to go`; small = `${m} months out — mark your calendar.`; }
  if (tagline && tagline.trim()) small = tagline.trim();
  return (
    <section data-testid="guest-countdown" className="px-6 py-10" style={{ background: wash }}>
      <div className="max-w-3xl mx-auto flex items-center justify-center gap-6 flex-wrap text-center">
        <div className="w-24 h-24 rounded-2xl bg-white flex flex-col items-center justify-center leading-none border" style={{ borderColor: `${accent}33` }}>
          <span className="uppercase tracking-widest text-[9px]" style={{ color: accent }}>Days</span>
          <span data-testid="guest-countdown-days" className="mt-1 text-4xl" style={{ fontFamily: fonts.serif, color: '#2A2A2A' }}>{days}</span>
        </div>
        <div className="min-w-0">
          <p className="text-2xl sm:text-3xl" style={{ fontFamily: fonts.serif, color: '#2A2A2A' }}>{big}</p>
          <p className="text-sm text-[#4A4A4A] mt-1 max-w-md">{small}</p>
        </div>
      </div>
    </section>
  );
}
